import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { useMutation, useQueries, useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';
import { object, string } from 'yup';
import { AxiosError, type AxiosResponse } from 'axios';

import { IconArrowLeft } from '@tabler/icons-react';

import { ButtonFieldIcon, SelectField } from '@wk/components-react16';

import useToast from '@/shared/stores/toastStore';

import {
  getCheckpoints,
  getSystemCheckpointConfigurations,
  updateSystemCheckpointConfiguration,
} from '@/domains/checkpoint/checkpointApi';

import type {
  CheckpointSystemStatus,
  ErrorResponse,
  UserRoleAccess,
} from '@/types';
import { UserRole } from '@/types';

import {
  getAvailableEvaluationViews,
  viewLabel,
  type EvaluationView,
} from '@/domains/checkpoint/checkpointViewAccess';
import { useEvaluationViews } from '@/domains/checkpoint/useEvaluationViews';

import CheckpointSettingsContent from './CheckpointSettingsContent';
import CheckpointSettingsModal from './CheckpointSettingsModal';
import type {
  CheckpointRow,
  CheckpointState,
  DateEditKind,
  MaintenanceCriticality,
} from './CheckpointSettingsPage.types';
import {
  buildCheckpointRows,
  getCurrentLocalDateTimeInput,
  isPastDateTimeInput,
  isTransitionAllowed,
  MAINTENANCE_CRITICALITY_OPTIONS,
  toDate,
  toLocalDateTimeInput,
} from './CheckpointSettingsPage.utils';

import './CheckpointSettingsPage.css';

const MOBILE_MEDIA_QUERY = '(max-width: 640px)';

interface CheckpointFormValues {
  comment: string;
  endDate: string;
  criticality: MaintenanceCriticality;
}

const Checkpoints = () => {
  const navigate = useNavigate();
  const [isMobileViewport, setIsMobileViewport] = useState(() => {
    if (typeof window === 'undefined') return false;
    return window.matchMedia(MOBILE_MEDIA_QUERY).matches;
  });
  const queryClient = useQueryClient();
  const userRoleAccess = queryClient.getQueryData<AxiosResponse<UserRoleAccess>>([
    'userRoleAccess',
  ]);
  const canManageCheckpoints =
    userRoleAccess?.data?.user?.roles?.includes(UserRole.CHECKPOINT_MANAGER) ??
    false;
  const { views } = useEvaluationViews();
  const availableViews = getAvailableEvaluationViews(
    views,
    userRoleAccess?.data?.user?.roles,
  );
  const [selectedView, setSelectedView] = useState<EvaluationView>('default');
  const { showToast } = useToast();

  const [
    {
      data: checkpointsData,
      isLoading: isCheckpointsLoading,
      refetch: refetchCheckpoints,
      error: checkpointsError,
    },
    {
      data: checkpointConfigsData,
      isLoading: isCheckpointConfigsLoading,
      refetch: refetchCheckpointConfigs,
      error: checkpointConfigsError,
    },
  ] = useQueries({
    queries: [
      {
        queryKey: ['checkpoints'],
        queryFn: () => getCheckpoints(),
      },
      {
        queryKey: ['system-checkpoint-configurations'],
        queryFn: () => getSystemCheckpointConfigurations(),
      },
    ],
  });

  const {
    mutate: updateCheckpointStatusFn,
    isPending: isUpdatingCheckpoint,
  } = useMutation({
    mutationFn: (data: {
      checkpointId: string;
      checkpointVersion?: string | number;
      status: CheckpointSystemStatus;
      reason: string;
      startDate?: string;
      endDate?: string;
      disabledStartDate?: string;
    }) => updateSystemCheckpointConfiguration(data),
    onSuccess: () => {
      showToast('success', {
        title: 'Checkpoint status updated',
        message: 'Checkpoint status updated successfully',
      });
    },
    onError: (error) => {
      if (error instanceof AxiosError) {
        const data = error.response?.data as
          | { title?: string; message?: string }
          | string
          | undefined;
        const apiMessage =
          (typeof data === 'string'
            ? data
            : data?.title || data?.message || (data ? JSON.stringify(data) : null)) ??
          null;

        showToast('error', {
          title: 'Error',
          message:
            apiMessage ||
            'An error occurred while updating the checkpoint status',
        });
      }
    },
  });

  const [state, setState] = useState<CheckpointState>({
    activeCheckpoint: null,
    targetStatus: null,
    isPreviewDialogOpen: false,
    dialogMode: 'MOVE',
    dateEditKind: null,
    openedAtIso: null,
  });

  const handleStateUpdate = (newState: Partial<CheckpointState>) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  };

  const {
    values,
    handleChange: handlePreviewChange,
    handleSubmit: handlePreviewSubmit,
    setFieldValue,
    errors,
    isValid,
  } = useFormik<CheckpointFormValues>({
    initialValues: {
      comment: '',
      endDate: '',
      criticality: 'MEDIUM',
    },
    validateOnChange: true,
    validateOnBlur: true,
    validateOnMount: true,
    validationSchema: object({
      comment: string().test(
        'comment-required',
        'Reason is required',
        (value) => {
          if (state.dialogMode === 'MOVE') {
            return Boolean((value ?? '').toString().trim());
          }
          return true;
        },
      ),
      endDate: string()
        .test('endDate-required', 'End date is required', (value) => {
          if (state.dialogMode === 'EDIT_DATE') {
            return Boolean((value ?? '').toString().trim());
          }
          return true;
        })
        .test('endDate-valid', 'End date is invalid', (value) => {
          if (state.dialogMode !== 'EDIT_DATE') return true;
          const raw = (value ?? '').toString().trim();
          if (!raw) return true;
          return Boolean(toDate(raw));
        })
        .test('endDate-not-past', 'End date cannot be in the past', (value) => {
          if (state.dialogMode !== 'EDIT_DATE') return true;
          const raw = (value ?? '').toString().trim();
          if (!raw) return true;
          return !isPastDateTimeInput(raw);
        }),
    }),
    onSubmit: (formValues) => {
      onPreviewSubmit(formValues);
    },
  });

  const commentValue = values.comment.toString().trim();
  const endDateValue = values.endDate.toString().trim();
  const isMoveDialog = state.dialogMode === 'MOVE';
  const isEditDateDialog = state.dialogMode === 'EDIT_DATE';
  const isSaveDisabled =
    isUpdatingCheckpoint ||
    !state.activeCheckpoint?.id ||
    !state.targetStatus ||
    !isValid ||
    (isMoveDialog && commentValue.length === 0) ||
    (isEditDateDialog && endDateValue.length === 0);

  const checkpoints = useMemo(() => {
    return buildCheckpointRows(checkpointsData, checkpointConfigsData);
  }, [checkpointConfigsData, checkpointsData]);

  const filteredCheckpoints = useMemo(() => {
    return checkpoints.filter((checkpoint) => {
      const views = checkpoint.views;
      if (!views || views.length === 0) return selectedView === 'default';
      return views.includes(selectedView);
    });
  }, [checkpoints, selectedView]);

  const handleBack = () => {
    navigate('/settings');
  };

  const openMoveModal = (
    row: CheckpointRow,
    targetStatus: CheckpointSystemStatus,
  ) => {
    if (!canManageCheckpoints) {
      showToast('info', {
        title: 'Access denied',
        message: 'Only Checkpoint Managers can update checkpoint statuses.',
      });
      return;
    }

    const currentStatus = row.status as CheckpointSystemStatus;
    if (!isTransitionAllowed(currentStatus, targetStatus)) {
      showToast('info', {
        title: 'Invalid status transition',
        message: `Cannot move checkpoint from ${currentStatus} to ${targetStatus}.`,
      });
      return;
    }

    handleStateUpdate({
      activeCheckpoint: row,
      targetStatus,
      isPreviewDialogOpen: true,
      dialogMode: 'MOVE',
      dateEditKind: null,
      openedAtIso: new Date().toISOString(),
    });
    setFieldValue('comment', '');
    setFieldValue('endDate', '');
    setFieldValue('criticality', 'MEDIUM');
  };

  const openEditDateModal = (row: CheckpointRow, kind: DateEditKind) => {
    if (!canManageCheckpoints) {
      showToast('info', {
        title: 'Access denied',
        message: 'Only Checkpoint Managers can update checkpoint statuses.',
      });
      return;
    }

    const currentStatus = (row.status as CheckpointSystemStatus) ?? 'LIVE';
    const requiredStatus = kind === 'PREVIEW_END' ? 'PREVIEW' : 'MAINTENANCE';
    if (currentStatus !== requiredStatus) {
      showToast('info', {
        title: 'Invalid action',
        message: `This action is only available for checkpoints in ${requiredStatus}.`,
      });
      return;
    }

    const existingEndDateInput = toLocalDateTimeInput(row.statusTooltip?.endDate);
    const currentDateInput = getCurrentLocalDateTimeInput();

    handleStateUpdate({
      activeCheckpoint: row,
      targetStatus: requiredStatus,
      isPreviewDialogOpen: true,
      dialogMode: 'EDIT_DATE',
      dateEditKind: kind,
      openedAtIso: null,
    });
    setFieldValue('comment', '');
    setFieldValue(
      'endDate',
      existingEndDateInput && !isPastDateTimeInput(existingEndDateInput)
        ? existingEndDateInput
        : currentDateInput,
    );
    setFieldValue('criticality', 'MEDIUM');
  };

  const onPreviewModalClose = () => {
    handleStateUpdate({
      isPreviewDialogOpen: false,
      activeCheckpoint: null,
      targetStatus: null,
      dialogMode: 'MOVE',
      dateEditKind: null,
      openedAtIso: null,
    });
    setFieldValue('comment', '');
    setFieldValue('endDate', '');
    setFieldValue('criticality', 'MEDIUM');
  };

  const onPreviewSubmit = (formValues: CheckpointFormValues) => {
    if (!canManageCheckpoints) {
      showToast('info', {
        title: 'Access denied',
        message: 'Only Checkpoint Managers can update checkpoint statuses.',
      });
      return;
    }

    const checkpointId = state.activeCheckpoint?.id || '';
    const checkpointVersion = state.activeCheckpoint?.version;
    const status = state.targetStatus;
    if (!checkpointId || !status) return;

    const addDaysIso = (baseIso: string, days: number) => {
      const base = new Date(baseIso);
      if (Number.isNaN(base.getTime())) return undefined;
      const ms = base.getTime() + days * 24 * 60 * 60 * 1000;
      return new Date(ms).toISOString();
    };

    const toIso = (input?: string) => {
      const date = toDate(input);
      if (!date) return undefined;
      return date.toISOString();
    };

    if (state.dialogMode === 'EDIT_DATE') {
      const existingReason = state.activeCheckpoint?.statusTooltip?.reason;
      if (!existingReason || !existingReason.toString().trim()) {
        showToast('error', {
          title: 'Missing reason',
          message:
            'Cannot update the date because the checkpoint has no reason set.',
        });
        return;
      }

      const endDateInput = formValues.endDate.toString().trim();
      if (isPastDateTimeInput(endDateInput)) {
        showToast('error', {
          title: 'Invalid end date',
          message: 'End date must be now or in the future.',
        });
        return;
      }

      const endDateIso = toIso(endDateInput);
      if (!endDateIso) {
        showToast('error', {
          title: 'Invalid end date',
          message: 'Please provide a valid end date.',
        });
        return;
      }

      updateCheckpointStatusFn(
        {
          checkpointId,
          checkpointVersion,
          status,
          reason: existingReason,
          startDate: state.activeCheckpoint?.statusTooltip?.startDate,
          endDate: endDateIso,
          disabledStartDate: status === 'MAINTENANCE' ? endDateIso : undefined,
        },
        {
          onSuccess: () => {
            onPreviewModalClose();
            refetchCheckpointConfigs();
            refetchCheckpoints();
          },
          onError: () => {},
        },
      );
      return;
    }

    const openedAtIso = state.openedAtIso || new Date().toISOString();
    const criticalityConfig = MAINTENANCE_CRITICALITY_OPTIONS.find(
      (option) => option.value === formValues.criticality,
    );
    const maintenanceDays = criticalityConfig?.days ?? 7;

    const endDateIso =
      status === 'PREVIEW'
        ? addDaysIso(openedAtIso, 30)
        : status === 'MAINTENANCE'
          ? addDaysIso(openedAtIso, maintenanceDays)
          : undefined;

    updateCheckpointStatusFn(
      {
        checkpointId,
        checkpointVersion,
        status,
        reason: formValues.comment,
        startDate: openedAtIso,
        endDate: endDateIso,
        disabledStartDate:
          status === 'MAINTENANCE'
            ? endDateIso
            : status === 'DISABLED'
              ? new Date().toISOString()
              : undefined,
      },
      {
        onSuccess: () => {
          onPreviewModalClose();
          refetchCheckpointConfigs();
          refetchCheckpoints();
        },
        onError: () => {},
      },
    );
  };

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia(MOBILE_MEDIA_QUERY);
    const onViewportChange = (event: MediaQueryListEvent) => {
      setIsMobileViewport(event.matches);
    };

    setIsMobileViewport(mediaQuery.matches);
    mediaQuery.addEventListener('change', onViewportChange);

    return () => {
      mediaQuery.removeEventListener('change', onViewportChange);
    };
  }, []);

  useEffect(() => {
    if (checkpointsError || checkpointConfigsError) {
      const checkpointsApiError = checkpointsError as AxiosError<ErrorResponse>;
      const checkpointConfigsApiError =
        checkpointConfigsError as AxiosError<ErrorResponse>;

      showToast('error', {
        title: 'Error',
        message: `${checkpointsApiError?.response?.data.title} - ${checkpointConfigsApiError?.response?.data.title}`,
      });
    }
  }, [checkpointConfigsError, checkpointsError, showToast]);

  return (
    <div className="checkpoints-page flex h-full flex-col gap-4 max-sm:h-auto max-sm:gap-3 max-sm:overflow-x-hidden">
      <div className="flex items-center justify-between gap-2">
        <h1 className="flex items-center gap-2 text-2xl font-bold">
          <ButtonFieldIcon>
            <button onClick={handleBack}>
              <IconArrowLeft size={28} />
            </button>
          </ButtonFieldIcon>
          Checkpoints
        </h1>
        <SelectField>
          <select
            id="checkpoint-view-filter"
            value={selectedView}
            onChange={(event) =>
              setSelectedView(event.target.value as EvaluationView)
            }
          >
            {availableViews.map((view) => (
              <option key={view} value={view}>
                {viewLabel(view)}
              </option>
            ))}
          </select>
        </SelectField>
      </div>

      <div className="flex h-[calc(100vh-4.25rem)] flex-1 min-h-0 flex-col gap-4 max-sm:h-auto max-sm:flex-none">
        <div className="h-full min-h-0 w-full sm:flex-1">
          <CheckpointSettingsContent
            checkpoints={filteredCheckpoints}
            isLoading={isCheckpointsLoading || isCheckpointConfigsLoading}
            isMobileViewport={isMobileViewport}
            canManageCheckpoints={canManageCheckpoints}
            onOpenMoveModal={openMoveModal}
            onOpenEditDateModal={openEditDateModal}
          />
        </div>
      </div>

      <CheckpointSettingsModal
        isOpen={state.isPreviewDialogOpen}
        activeCheckpoint={state.activeCheckpoint}
        targetStatus={state.targetStatus}
        dialogMode={state.dialogMode}
        openedAtIso={state.openedAtIso}
        comment={values.comment}
        endDate={values.endDate}
        criticality={values.criticality}
        endDateError={state.dialogMode === 'EDIT_DATE' ? errors.endDate : undefined}
        isUpdatingCheckpoint={isUpdatingCheckpoint}
        isSaveDisabled={isSaveDisabled}
        onChange={handlePreviewChange}
        onSubmit={() => {
          handlePreviewSubmit();
        }}
        onClose={onPreviewModalClose}
      />
    </div>
  );
};

export default Checkpoints;
